import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, MapPin, Thermometer, Wind, Droplets, Sun, CloudRain, CloudSnow, Cloud, CloudLightning, Loader2, LocateFixed } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const codeToLabel = (c) => {
  const map = { 0:"Clear",1:"Mostly clear",2:"Partly cloudy",3:"Overcast",45:"Fog",48:"Rime fog",51:"Light drizzle",53:"Drizzle",55:"Heavy drizzle",61:"Light rain",63:"Rain",65:"Heavy rain",71:"Wintry mix",73:"Snow",75:"Heavy snow",80:"Rain showers",81:"Heavy showers",82:"Violent showers",95:"Thunderstorm",96:"T-storm w/ hail",99:"Severe t-storm" };
  return map[c] || "—";
};
const codeIcon = (c, cls="w-5 h-5") => {
  if ([0,1].includes(c)) return <Sun className={cls} />;
  if ([2,3,45,48].includes(c)) return <Cloud className={cls} />;
  if ([51,53,55,61,63,65,80,81,82].includes(c)) return <CloudRain className={cls} />;
  if ([71,73,75].includes(c)) return <CloudSnow className={cls} />;
  if ([95,96,99].includes(c)) return <CloudLightning className={cls} />;
  return <Cloud className={cls} />;
};

const dayName = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" });
const timeShort = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric" });
const show = (value, suffix = "") => (value === null || value === undefined ? "—" : `${Math.round(value)}${suffix}`);

const DEFAULT_PLACE = { name: "Oklahoma City", admin1: "Oklahoma", latitude: 35.4676, longitude: -97.5164 };

const getDeviceCoords = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, origin: "device" }),
    () => resolve(null),
    { timeout: 8000, maximumAge: 300000 },
  );
});

const getIpCoords = async () => {
  try {
    const r = await fetch(`${API}/weather/iplocate`);
    if (!r.ok) return null;
    const d = await r.json();
    if (d?.latitude && d?.longitude) {
      return { latitude: d.latitude, longitude: d.longitude, name: d.name, admin1: d.admin1, origin: "ip" };
    }
  } catch (_) {}
  return null;
};

const Forecast = () => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [place, setPlace] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState("");
  const skipSuggest = useRef(false);

  useEffect(() => {
    if (skipSuggest.current) { skipSuggest.current = false; setSuggestions([]); return; }
    if (query.trim().length < 2) { setSuggestions([]); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/weather/geocode?q=${encodeURIComponent(query)}`, { signal: ctrl.signal });
        const j = await r.json();
        setSuggestions(Array.isArray(j) ? j : []);
      } catch(_) {}
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query]);

  const loadForecast = async (p) => {
    skipSuggest.current = true;
    setLoading(true); setError(""); setData(null); setPlace(p); setSuggestions([]);
    setQuery(p.name ? `${p.name}${p.admin1 ? ", " + p.admin1 : ""}` : "Your location");
    try {
      const r = await fetch(`${API}/weather/forecast?lat=${p.latitude}&lon=${p.longitude}`);
      if (!r.ok) throw new Error("Forecast is temporarily unavailable. Please try again.");
      const payload = await r.json();
      setData(payload);
      if (!p.name && payload?.place?.name) {
        const resolved = { ...p, name: payload.place.name, admin1: payload.place.admin1 };
        setPlace(resolved);
        skipSuggest.current = true;
        setQuery(`${resolved.name}${resolved.admin1 ? ", " + resolved.admin1 : ""}`);
      }
    } catch(e) {
      setError(e.message || "Could not load forecast. Please try again.");
    }
    setLoading(false);
  };

  const detectLocation = async () => {
    setLocating(true);
    const coords = (await getDeviceCoords()) || (await getIpCoords());
    setLocating(false);
    await loadForecast(coords || DEFAULT_PLACE);
  };

  useEffect(() => {
    detectLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hours = useMemo(() => data?.hourly || [], [data]);
  const days = useMemo(() => data?.daily || [], [data]);
  const current = data?.current;
  const placeLabel = place ? `${place.name}${place.admin1 ? ", " + place.admin1 : ""}` : "";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8" data-testid="forecast-page">
      <div>
        <div className="text-[11px] mono uppercase tracking-widest text-cyan-400">Storm Desk Forecast</div>
        <h1 className="display-italic text-4xl sm:text-5xl mt-1 uppercase sd-title-gradient">Local weather forecast</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">
          Your local forecast loads automatically from your device location. Search any other city or county anytime.
          {data?.source ? ` Data: ${data.source}.` : ""}
        </p>
      </div>

      <div className="relative mt-6 max-w-2xl">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="forecast-search-input"
              value={query}
              onChange={(e)=>setQuery(e.target.value)}
              placeholder="Search city, county, or town…"
              className="pl-9 bg-black/40 border-white/10 text-slate-100 h-11"
            />
          </div>
          <Button
            data-testid="forecast-search-button"
            onClick={()=>suggestions[0] && loadForecast(suggestions[0])}
            disabled={!suggestions.length}
            className="bg-red-600 hover:bg-red-500 text-white h-11 px-5"
          >
            Search
          </Button>
          <Button
            data-testid="forecast-use-location-button"
            onClick={detectLocation}
            disabled={locating}
            variant="outline"
            title="Use my device location"
            className="h-11 px-4 gap-2 border-white/15 bg-white/[0.03] text-slate-100 hover:bg-white/[0.08]"
          >
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4 text-cyan-400" />}
            <span className="hidden sm:inline">My location</span>
          </Button>
        </div>
        {suggestions.length > 0 && (
          <div className="absolute z-30 mt-1 w-full glass rounded-lg overflow-hidden border border-white/10 shadow-xl">
            {suggestions.map((s,i)=>(
              <button
                key={`${s.id}-${i}`}
                data-testid={`forecast-suggestion-${i}`}
                onClick={()=>loadForecast(s)}
                className="w-full text-left px-3 py-2.5 hover:bg-white/10 flex items-center gap-2 text-sm"
              >
                <MapPin className="w-4 h-4 text-cyan-400" />
                <div className="flex-1">
                  <div className="text-slate-100">{s.name}{s.admin1?`, ${s.admin1}`:""}</div>
                  <div className="text-[11px] mono text-slate-500">{s.country} · {Number(s.latitude).toFixed(2)}, {Number(s.longitude).toFixed(2)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {locating && (
        <div className="mt-10 flex items-center gap-2 text-slate-400 text-sm" data-testid="forecast-locating">
          <Loader2 className="w-4 h-4 animate-spin" /> Finding your location…
        </div>
      )}
      {loading && !locating && (
        <div className="mt-10 flex items-center gap-2 text-slate-400 text-sm" data-testid="forecast-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading forecast…
        </div>
      )}
      {error && (
        <div data-testid="forecast-error-message" className="mt-6 sd-panel rounded-xl p-5">
          <div className="text-red-400 text-sm">{error}</div>
          <Button onClick={()=>loadForecast(place || DEFAULT_PLACE)} className="mt-3 h-9 bg-red-600 hover:bg-red-500 text-white text-sm">Retry</Button>
        </div>
      )}

      {current && (
        <div className="mt-8 space-y-6">
          <div className="glass rounded-2xl p-5 sm:p-6" data-testid="forecast-current-panel">
            <div className="text-[11px] mono uppercase tracking-widest text-slate-500 flex items-center gap-2">
              Current conditions
              {place?.origin && (
                <span data-testid="forecast-location-origin" className="inline-flex items-center gap-1 text-cyan-400">
                  <LocateFixed className="w-3 h-3" /> {place.origin === "device" ? "Device location" : "Approximate location"}
                </span>
              )}
            </div>
            <div className="display text-2xl font-bold mt-0.5">{placeLabel}</div>
            <div className="mt-4 flex items-end gap-8 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="text-cyan-300">{codeIcon(current.code, "w-12 h-12")}</div>
                <div>
                  <div className="display text-6xl font-extrabold leading-none tabular-nums">{show(current.temp_f, "°")}</div>
                  <div className="text-slate-400 mono text-[11px] mt-1">Feels like {show(current.feels_like_f, "°F")}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 min-w-[240px]">
                <div className="flex items-center gap-2 text-sm"><Wind className="w-4 h-4 text-slate-400" /><span className="mono">{show(current.wind_mph)} mph</span></div>
                <div className="flex items-center gap-2 text-sm"><Droplets className="w-4 h-4 text-slate-400" /><span className="mono">{show(current.humidity, "%")}</span></div>
                <div className="flex items-center gap-2 text-sm"><Thermometer className="w-4 h-4 text-slate-400" /><span className="mono">{show(current.pressure_hpa)} hPa</span></div>
                <div className="flex items-center gap-2 text-sm text-slate-300">{current.label || codeToLabel(current.code)}</div>
              </div>
            </div>
            {current.station && (
              <div className="mt-4 text-[10px] mono uppercase tracking-widest text-slate-600">
                Station {current.station}{current.observed_at ? ` · observed ${new Date(current.observed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
              </div>
            )}
          </div>

          {hours.length > 0 && (
            <div className="glass rounded-2xl p-5" data-testid="forecast-hourly-panel">
              <div className="text-[11px] mono uppercase tracking-widest text-slate-500 mb-3">Next 24 hours</div>
              <div className="flex overflow-x-auto gap-2 pb-2">
                {hours.map((h,i)=>(
                  <div key={h.time || i} className="min-w-[86px] px-3 py-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                    <div className="text-[11px] mono text-slate-400">{i===0?"Now":timeShort(h.time)}</div>
                    <div className="my-1.5 text-cyan-300 flex justify-center">{codeIcon(h.code, "w-5 h-5")}</div>
                    <div className="font-semibold tabular-nums">{show(i === 0 && current?.temp_f != null ? current.temp_f : h.temp_f, "°")}</div>
                    <div className="text-[10px] mono text-cyan-400 mt-0.5">{h.pop ?? 0}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {days.length > 0 && (
            <div className="glass rounded-2xl p-5" data-testid="forecast-daily-panel">
              <div className="text-[11px] mono uppercase tracking-widest text-slate-500 mb-3">7-day outlook</div>
              <div className="divide-y divide-white/5">
                {days.map((d,i)=>(
                  <div key={d.date} className="py-3 grid grid-cols-[70px_36px_1fr_70px_90px] items-center gap-3">
                    <div className="text-slate-200 font-medium">{i===0?"Today":dayName(d.date)}</div>
                    <div className="text-cyan-300">{codeIcon(d.code, "w-5 h-5")}</div>
                    <div className="text-sm text-slate-300 truncate">{d.label || codeToLabel(d.code)}</div>
                    <div className="text-[11px] mono text-cyan-400 text-right">{d.pop ?? 0}%</div>
                    <div className="font-mono text-right tabular-nums">
                      <span className="text-slate-100 font-semibold">{show(d.high_f, "°")}</span>
                      <span className="text-slate-500 mx-1">/</span>
                      <span className="text-slate-400">{show(d.low_f, "°")}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Forecast;
